import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Pause, Volume2, VolumeX, Maximize2 } from 'lucide-react';

interface Scene {
  id: number;
  title: string;
  subtitle: string;
  emoji: string;
  color: string;
  gradient: string;
}

const SCENES: Scene[] = [
  {
    id: 1,
    title: "Stuck in Lagos Go-Slow?",
    subtitle: "Third Mainland Bridge gridlock. 2 hours wasted.",
    emoji: "🚦",
    color: "from-red-600 to-orange-600",
    gradient: "from-red-500/20 to-orange-500/20"
  },
  {
    id: 2,
    title: "NEPA Takes Light Again",
    subtitle: "6 hours darkness. Generator fuel running low.",
    emoji: "⚡",
    color: "from-yellow-600 to-amber-600",
    gradient: "from-yellow-500/20 to-amber-500/20"
  },
  {
    id: 3,
    title: "Fuel Scarcity Everywhere",
    subtitle: "Long queues. Empty stations. Rising prices.",
    emoji: "⛽",
    color: "from-purple-600 to-pink-600",
    gradient: "from-purple-500/20 to-pink-500/20"
  },
  {
    id: 4,
    title: "Flooding Blocks Your Route",
    subtitle: "Admiralty Way waist-deep. Cars stranded.",
    emoji: "💧",
    color: "from-cyan-600 to-blue-600",
    gradient: "from-cyan-500/20 to-blue-500/20"
  },
  {
    id: 5,
    title: "Sentlo Changes Everything",
    subtitle: "Real-time intelligence from your neighbors",
    emoji: "🇳🇬",
    color: "from-emerald-600 to-teal-600",
    gradient: "from-emerald-500/20 to-teal-500/20"
  }
];

interface AdvertVideoProps {
  autoPlay?: boolean;
  onEnded?: () => void;
}

export function AdvertVideo({ autoPlay = false, onEnded }: AdvertVideoProps) {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [currentScene, setCurrentScene] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);

  const SCENE_DURATION = 3000; // 3 seconds per scene
  const TOTAL_DURATION = SCENES.length * SCENE_DURATION;

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentScene((prev) => {
        const next = prev + 1;
        if (next >= SCENES.length) {
          setIsPlaying(false);
          setProgress(100);
          onEnded?.();
          return prev;
        }
        return next;
      });
    }, SCENE_DURATION);

    return () => clearInterval(interval);
  }, [isPlaying, onEnded]);

  useEffect(() => {
    if (!isPlaying) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 / TOTAL_DURATION) * 100;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [isPlaying, TOTAL_DURATION]);

  const handlePlayPause = () => {
    if (progress >= 100) {
      // Restart
      setCurrentScene(0);
      setProgress(0);
    }
    setIsPlaying(!isPlaying);
  };

  const scene = SCENES[currentScene];

  return (
    <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
      {/* Video Content */}
      <div className={`absolute inset-0 bg-gradient-to-br ${scene.color}`}>
        <div className={`absolute inset-0 bg-gradient-to-t ${scene.gradient} backdrop-blur-3xl`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={scene.id}
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.2, y: -50 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
            >
              {/* Emoji */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="text-8xl md:text-9xl mb-6 drop-shadow-2xl"
              >
                {scene.emoji}
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-3xl md:text-5xl font-black text-white mb-4 drop-shadow-lg"
              >
                {scene.title}
              </motion.h2>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="text-lg md:text-2xl text-white/90 font-semibold max-w-2xl drop-shadow-md"
              >
                {scene.subtitle}
              </motion.p>

              {/* Final Scene CTA */}
              {scene.id === 5 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8, type: "spring" }}
                  className="mt-8"
                >
                  <div className="bg-white text-emerald-600 px-8 py-4 rounded-full font-black text-xl md:text-2xl shadow-2xl">
                    Community-Powered Survival Tool
                  </div>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white/20 rounded-full"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                }}
                animate={{
                  y: [null, Math.random() * window.innerHeight],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Video Controls Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none">
        {/* Top Bar - Scene Indicators */}
        <div className="absolute top-4 left-4 right-4 flex gap-1">
          {SCENES.map((_, idx) => (
            <div
              key={idx}
              className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
            >
              <motion.div
                className="h-full bg-white rounded-full"
                initial={{ width: "0%" }}
                animate={{
                  width: idx < currentScene ? "100%" : idx === currentScene ? `${(progress % (100 / SCENES.length)) * SCENES.length}%` : "0%"
                }}
                transition={{ duration: 0.1 }}
              />
            </div>
          ))}
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-auto">
          <div className="flex items-center gap-3">
            {/* Play/Pause */}
            <button
              onClick={handlePlayPause}
              className="w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all hover:scale-110 active:scale-95"
            >
              {isPlaying ? (
                <Pause className="size-6 text-gray-900 ml-0.5" fill="currentColor" />
              ) : (
                <Play className="size-6 text-gray-900 ml-1" fill="currentColor" />
              )}
            </button>

            {/* Progress Bar */}
            <div className="flex-1 h-2 bg-white/30 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-white rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Volume */}
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
            >
              {isMuted ? (
                <VolumeX className="size-5 text-white" />
              ) : (
                <Volume2 className="size-5 text-white" />
              )}
            </button>

            {/* Fullscreen */}
            <button
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
            >
              <Maximize2 className="size-5 text-white" />
            </button>
          </div>
        </div>

        {/* Sentlo Logo Watermark */}
        <div className="absolute top-4 left-4 pointer-events-none">
          <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
            <span className="text-white font-black text-sm">SENTLO</span>
          </div>
        </div>
      </div>
    </div>
  );
}
